import { useState, useEffect } from "react";
import { api } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, FileText, DollarSign, AlertCircle, CheckCircle, Clock, Download, Mail, Printer, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { formatDate, toDateOnlyString, fromDateOnlyString, calculateDueDate, getTodayString } from "../utils/dates";
import { formatCurrency } from "../utils/formatters";

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Partial invoicing states
  const [partialDialogOpen, setPartialDialogOpen] = useState(false);
  const [selectedInvoiceForPartial, setSelectedInvoiceForPartial] = useState(null);
  const [partialPercentage, setPartialPercentage] = useState(25);
  const [partialDueDate, setPartialDueDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Status change state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedInvoiceForStatus, setSelectedInvoiceForStatus] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  
  const [formData, setFormData] = useState({
    client_name: "",
    client_company: "",
    client_email: "",
    client_address: "",
    project_description: "",
    invoice_date: getTodayString(),
    due_date: "",
    due_terms: "net_30",
    tax_percentage: "0",
    notes: "",
    line_items: [{ description: "", quantity: "1", unit_price: "", subtotal: 0, unit_type: "quantity" }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, statsRes] = await Promise.all([
        api.get("/invoices"),
        api.get("/invoices/stats/summary"),
      ]);
      setInvoices(invoicesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch invoice data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (invoice = null) => {
    if (invoice) {
      setEditingInvoice(invoice);
      setFormData({
        client_name: invoice.client_name,
        client_company: invoice.client_company || "",
        client_email: invoice.client_email || "",
        client_address: invoice.client_address || "",
        project_description: invoice.project_description,
        invoice_date: invoice.invoice_date,
        due_date: invoice.due_date || "",
        due_terms: invoice.due_terms || "net_30",
        tax_percentage: invoice.tax_percentage.toString(),
        notes: invoice.notes || "",
        line_items: invoice.line_items.map(item => ({
          ...item,
          unit_type: item.unit_type || "quantity"
        }))
      });
    } else {
      setEditingInvoice(null);
      const today = getTodayString();
      const dueDate = calculateDueDate(today, "net_30");
      
      setFormData({
        client_name: "",
        client_company: "",
        client_email: "",
        client_address: "",
        project_description: "",
        invoice_date: today,
        due_date: dueDate,
        due_terms: "net_30",
        tax_percentage: "0",
        notes: "",
        line_items: [{ description: "", quantity: "1", unit_price: "", subtotal: 0, unit_type: "quantity" }]
      });
    }
    setDialogOpen(true);
  };

  const handleDueTermsChange = (terms) => {
    setFormData(prev => {
      const newData = { ...prev, due_terms: terms };
      
      // Calculate due_date based on terms
      if (terms === 'upon_receipt') {
        newData.due_date = null;
      } else if (terms === 'custom') {
        // Keep current due_date or set to 30 days if empty
        if (!newData.due_date && newData.invoice_date) {
          newData.due_date = calculateDueDate(newData.invoice_date, 'net_30');
        }
      } else {
        // net_7, net_15, net_30
        if (newData.invoice_date) {
          newData.due_date = calculateDueDate(newData.invoice_date, terms);
        }
      }
      
      return newData;
    });
  };


  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingInvoice(null);
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: "", quantity: "1", unit_price: "", subtotal: 0, unit_type: "quantity" }]
    });
  };

  const removeLineItem = (index) => {
    const newLineItems = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: newLineItems });
  };

  const updateLineItem = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index][field] = value;
    
    // Bidirectional calculation
    if (field === 'quantity' || field === 'unit_price') {
      // Editing quantity or unit_price → calculate subtotal
      const qty = parseFloat(newLineItems[index].quantity) || 0;
      const price = parseFloat(newLineItems[index].unit_price) || 0;
      newLineItems[index].subtotal = qty * price;
    } else if (field === 'subtotal') {
      // Editing subtotal → calculate unit_price (if quantity > 0)
      const qty = parseFloat(newLineItems[index].quantity) || 0;
      const subtotal = parseFloat(value) || 0;
      if (qty > 0) {
        newLineItems[index].unit_price = (subtotal / qty).toFixed(2);
      }
      newLineItems[index].subtotal = subtotal;
    }
    
    setFormData({ ...formData, line_items: newLineItems });
  };

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const taxAmount = subtotal * (parseFloat(formData.tax_percentage) / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        client_name: formData.client_name,
        client_company: formData.client_company || null,
        client_email: formData.client_email || null,
        client_address: formData.client_address || null,
        project_description: formData.project_description,
        invoice_date: formData.invoice_date,
        due_date: formData.due_terms === 'upon_receipt' ? null : formData.due_date,
        due_terms: formData.due_terms,
        tax_percentage: parseFloat(formData.tax_percentage),
        notes: formData.notes || null,
        line_items: formData.line_items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          subtotal: item.subtotal,
          unit_type: item.unit_type || "quantity"
        }))
      };

      if (editingInvoice) {
        await api.put(`/invoices/${editingInvoice.id}`, payload);
        toast.success("Invoice updated successfully");
      } else {
        await api.post("/invoices", payload);
        toast.success("Invoice created successfully");
      }

      fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving invoice:", error);
      toast.error("Failed to save invoice");
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) {
      return;
    }

    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success("Invoice deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { payment_status: "paid" });
      toast.success("Invoice marked as paid");
      fetchData();
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error("Failed to update invoice");
    }
  };


  // Status change handler
  const openStatusDialog = (invoice) => {
    setSelectedInvoiceForStatus(invoice);
    setNewStatus(invoice.payment_status);
    setAmountPaid(invoice.amount_paid || "");
    setStatusDialogOpen(true);
  };

  const handleStatusChange = async () => {
    try {
      const payload = { payment_status: newStatus };
      if (newStatus === "partially_paid" && amountPaid) {
        payload.amount_paid = parseFloat(amountPaid);
      }
      
      await api.put(`/invoices/${selectedInvoiceForStatus.id}/status`, payload);
      toast.success(`Invoice status updated to ${newStatus}`);
      setStatusDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  // Partial invoicing handlers
  const openPartialDialog = (invoice) => {
    setSelectedInvoiceForPartial(invoice);
    setPartialPercentage(25);
    setPartialDueDate(new Date().toISOString().split('T')[0]);
    setPartialDialogOpen(true);
  };

  const handleCreatePartial = async () => {
    try {
      const response = await api.post(`/invoices/${selectedInvoiceForPartial.id}/create-partial`, {
        percentage: partialPercentage,
        due_date: partialDueDate
      });
      toast.success(response.data.message);
      setPartialDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error creating partial invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to create partial invoice");
    }
  };

  const handleCreateBalance = async (invoiceId) => {
    if (!window.confirm("Create a balance invoice for the remaining amount?")) return;
    
    try {
      const response = await api.post(`/invoices/${invoiceId}/create-balance`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error("Error creating balance invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to create balance invoice");
    }
  };

  const handleCheckOverdue = async () => {
    try {
      const response = await api.post("/invoices/check-overdue");
      toast.success(response.data.message + ` (${response.data.updated_count} invoices updated)`);
      fetchData();
    } catch (error) {
      console.error("Error checking overdue:", error);
      toast.error("Failed to check overdue invoices");
    }
  };


  const handleViewInvoice = (invoice) => {
    setViewingInvoice(invoice);
    setViewDialogOpen(true);
  };

  const handleViewPDF = async () => {
    if (!viewingInvoice) return;
    
    try {
      // Open PDF in new tab for viewing/printing
      const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${viewingInvoice.id}/pdf`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error opening PDF:', error);
      toast.error("Failed to open PDF");
    }
  };

  const handleDownloadPDF = async () => {
    if (!viewingInvoice) return;
    
    try {
      const response = await api.get(`/invoices/${viewingInvoice.id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create a download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${viewingInvoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  const handleEmailInvoice = () => {
    if (!viewingInvoice) return;
    
    const subject = encodeURIComponent(`Invoice ${viewingInvoice.invoice_number} from Cleanifax Inc.`);
    const body = encodeURIComponent(`
Dear ${viewingInvoice.client_name},

Please find attached invoice ${viewingInvoice.invoice_number} for ${viewingInvoice.project_description}.

Invoice Details:
- Amount Due: ${formatCurrency(viewingInvoice.total)}
- Due Date: ${viewingInvoice.due_terms === 'upon_receipt' ? 'Due Upon Receipt' : formatDate(fromDateOnlyString(viewingInvoice.due_date))}

You can view and download your invoice at: ${window.location.origin}/invoices

If you have any questions, please contact:
Grishma Chokshi
Phone: 902-223-7377
Email: admin@cleanifax.ca

Thank you for your business!

Best regards,
Cleanifax Inc.
Post-Construction & Final Cleaning Specialists
    `);
    
    const mailto = `mailto:${viewingInvoice.client_email || ''}?subject=${subject}&body=${body}`;
    window.location.href = mailto;
    
    toast.success("Email client opened. Please attach the PDF and send.");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'border-green-300 bg-green-50 text-green-700';
      case 'unpaid':
        return 'border-orange-300 bg-orange-50 text-orange-700';
      case 'overdue':
        return 'border-red-300 bg-red-50 text-red-700';
      default:
        return 'border-slate-300 bg-slate-50 text-slate-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-4 w-4" />;
      case 'unpaid':
        return <Clock className="h-4 w-4" />;
      case 'overdue':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesTab = activeTab === "all" || invoice.payment_status === activeTab;
    const matchesSearch = 
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (invoice.client_company && invoice.client_company.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesTab && matchesSearch;
  });

  const { subtotal, taxAmount, total } = formData.line_items ? calculateTotals() : { subtotal: 0, taxAmount: 0, total: 0 };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="invoices-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Invoices</h1>
          <p className="text-slate-600 mt-1">Manage client invoices and payments</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleCheckOverdue}
            variant="outline"
            className="text-orange-600 border-orange-600"
          >
            <AlertCircle className="h-4 w-4 mr-2" />
            Check Overdue
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
            data-testid="create-invoice-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Invoice
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Invoiced</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  ${stats?.total_invoiced.toLocaleString() || 0}
                </p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Received</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  ${stats?.total_received.toLocaleString() || 0}
                </p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Outstanding</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">
                  ${stats?.outstanding_balance.toLocaleString() || 0}
                </p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Overdue</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {stats?.overdue_count || 0}
                </p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Input
              placeholder="Search by invoice number, client name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
              data-testid="invoice-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Invoice List with Tabs */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>Invoice List</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">All ({stats?.total_count || 0})</TabsTrigger>
              <TabsTrigger value="paid">Paid ({stats?.paid_count || 0})</TabsTrigger>
              <TabsTrigger value="unpaid">Unpaid ({stats?.unpaid_count || 0})</TabsTrigger>
              <TabsTrigger value="overdue">Overdue ({stats?.overdue_count || 0})</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          No invoices found. Create your first invoice to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredInvoices.map((invoice) => (
                        <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                          <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{invoice.client_name}</div>
                              {invoice.client_company && (
                                <div className="text-sm text-slate-500">{invoice.client_company}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {invoice.project_description.substring(0, 40)}
                            {invoice.project_description.length > 40 && "..."}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDate(fromDateOnlyString(invoice.invoice_date))}
                          </TableCell>
                          <TableCell className="text-sm">
                            {invoice.due_terms === 'upon_receipt' ? (
                              <span className="text-[#247C75] font-medium">Due Upon Receipt</span>
                            ) : invoice.due_date ? (
                              formatDate(fromDateOnlyString(invoice.due_date))
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell className="font-semibold text-slate-900">
                            <div>
                              <div>{formatCurrency(invoice.total)}</div>
                              {invoice.payment_status === 'partially_paid' && invoice.balance_due > 0 && (
                                <div className="text-xs text-orange-600">
                                  Balance: {formatCurrency(invoice.balance_due)}
                                </div>
                              )}
                              {invoice.is_partial && (
                                <Badge variant="outline" className="text-xs mt-1">Partial</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getStatusColor(invoice.payment_status)}>
                              <span className="flex items-center gap-1">
                                {getStatusIcon(invoice.payment_status)}
                                {invoice.payment_status}
                              </span>
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewInvoice(invoice)}
                                data-testid={`view-invoice-${invoice.id}`}
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openStatusDialog(invoice)}
                                className="text-blue-600"
                                title="Change Status"
                              >
                                <Clock className="h-4 w-4" />
                              </Button>
                              {(invoice.payment_status === 'draft' || invoice.payment_status === 'sent') && !invoice.is_partial && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openPartialDialog(invoice)}
                                  className="text-purple-600"
                                  title="Create Partial Invoice"
                                >
                                  <DollarSign className="h-4 w-4" />
                                </Button>
                              )}
                              {invoice.payment_status === 'partially_paid' && invoice.balance_due > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCreateBalance(invoice.id)}
                                  className="text-orange-600"
                                  title="Create Balance Invoice"
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenDialog(invoice)}
                                data-testid={`edit-invoice-${invoice.id}`}
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(invoice.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-invoice-${invoice.id}`}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Create/Edit Invoice Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingInvoice ? "Edit Invoice" : "Create New Invoice"}
            </DialogTitle>
            <DialogDescription>
              {editingInvoice ? "Update invoice details" : "Fill in the details to create a new invoice"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Client Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Client Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_name">Client Name *</Label>
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_company">Company Name</Label>
                    <Input
                      id="client_company"
                      value={formData.client_company}
                      onChange={(e) => setFormData({ ...formData, client_company: e.target.value })}
                      data-testid="client-company-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_email">Client Email</Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    data-testid="client-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_address">Client Address</Label>
                  <Textarea
                    id="client_address"
                    value={formData.client_address}
                    onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                    rows={2}
                    placeholder="Street address, City, Province, Postal Code"
                    data-testid="client-address-input"
                  />
                </div>
              </div>

              {/* Invoice Details */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-slate-900">Invoice Details</h3>
                <div className="space-y-2">
                  <Label htmlFor="project_description">Project Description *</Label>
                  <Textarea
                    id="project_description"
                    value={formData.project_description}
                    onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                    required
                    rows={2}
                    data-testid="project-description-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="invoice_date">Invoice Date *</Label>
                    <Input
                      id="invoice_date"
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => {
                        const newInvoiceDate = e.target.value;
                        setFormData(prev => {
                          const newData = { ...prev, invoice_date: newInvoiceDate };
                          // Recalculate due_date if using terms
                          if (prev.due_terms && prev.due_terms !== 'upon_receipt' && prev.due_terms !== 'custom') {
                            newData.due_date = calculateDueDate(newInvoiceDate, prev.due_terms);
                          }
                          return newData;
                        });
                      }}
                      required
                      data-testid="invoice-date-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="due_terms">Payment Terms *</Label>
                    <Select
                      value={formData.due_terms}
                      onValueChange={handleDueTermsChange}
                    >
                      <SelectTrigger id="due_terms">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upon_receipt">Due Upon Receipt</SelectItem>
                        <SelectItem value="net_7">Net 7 Days</SelectItem>
                        <SelectItem value="net_15">Net 15 Days</SelectItem>
                        <SelectItem value="net_30">Net 30 Days</SelectItem>
                        <SelectItem value="custom">Custom Date...</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.due_terms === 'custom' && (
                    <div className="space-y-2">
                      <Label htmlFor="due_date">Due Date *</Label>
                      <Input
                        id="due_date"
                        type="date"
                        value={formData.due_date || ''}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        required
                        data-testid="due-date-input"
                      />
                    </div>
                  )}
                  
                  {formData.due_terms !== 'upon_receipt' && formData.due_terms !== 'custom' && formData.due_date && (
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Calculated Due Date</Label>
                      <div className="text-sm font-medium text-slate-900 py-2">
                        {formatDate(fromDateOnlyString(formData.due_date))}
                      </div>
                    </div>
                  )}
                  
                  {formData.due_terms === 'upon_receipt' && (
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Due Date</Label>
                      <div className="text-sm font-medium text-[#247C75] py-2">
                        Due Upon Receipt
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="tax_percentage">Tax % *</Label>
                    <Input
                      id="tax_percentage"
                      type="number"
                      step="0.01"
                      value={formData.tax_percentage}
                      onChange={(e) => setFormData({ ...formData, tax_percentage: e.target.value })}
                      required
                      data-testid="tax-percentage-input"
                    />
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-slate-900">Line Items</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLineItem}
                    data-testid="add-line-item-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>
                
                {formData.line_items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end border border-slate-200 p-3 rounded-lg">
                    <div className="col-span-5 space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        required
                        data-testid={`line-item-description-${index}`}
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Quantity / Area</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                        required
                        data-testid={`line-item-quantity-${index}`}
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Unit Type</Label>
                      <Select
                        value={item.unit_type || "quantity"}
                        onValueChange={(value) => updateLineItem(index, 'unit_type', value)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="quantity">Qty</SelectItem>
                          <SelectItem value="sqft">Sq.ft</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Unit Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                        required
                        data-testid={`line-item-price-${index}`}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {formatCurrency(parseFloat(item.unit_price) || 0)}
                      </p>
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Subtotal</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.subtotal}
                        onChange={(e) => updateLineItem(index, 'subtotal', e.target.value)}
                        disabled={parseFloat(item.quantity) === 0}
                        className={parseFloat(item.quantity) === 0 ? "bg-slate-50" : ""}
                        data-testid={`line-item-subtotal-${index}`}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {parseFloat(item.quantity) === 0 
                          ? "Enter quantity first" 
                          : formatCurrency(item.subtotal || 0)}
                      </p>
                    </div>
                    <div className="col-span-1">
                      {formData.line_items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLineItem(index)}
                          className="text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Tax ({formData.tax_percentage}%):</span>
                  <span className="font-medium">${taxAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total:</span>
                  <span className="text-blue-600">${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2 pt-4 border-t">
                <Label htmlFor="notes">Notes / Terms</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Payment terms, additional notes..."
                  data-testid="notes-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                data-testid="invoice-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="invoice-save-button"
              >
                {editingInvoice ? "Update Invoice" : "Create Invoice"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      {viewingInvoice && (
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto print:shadow-none" data-testid="view-invoice-dialog">
            <DialogHeader className="print:hidden">
              <DialogTitle>Invoice {viewingInvoice.invoice_number}</DialogTitle>
              <DialogDescription>
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleViewPDF}
                    data-testid="view-print-invoice-button"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    View/Print PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleEmailInvoice}
                    data-testid="email-invoice-button"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownloadPDF}
                    data-testid="download-pdf-button"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </DialogDescription>
            </DialogHeader>
            
            {/* Invoice Preview */}
            <div className="bg-white p-8 border border-slate-200 rounded-lg print:border-0" data-testid="invoice-preview-content">
              {/* Header */}
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h1 className="text-3xl font-bold text-blue-600">CLEANIFAX INC.</h1>
                  <p className="text-sm text-slate-600 mt-1">Post-Construction & Final Cleaning Specialists</p>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_clean-crew-tracker/artifacts/tzw4ofm2_Final_cleanifax_logo_small.png" 
                    alt="Cleanifax Logo" 
                    className="h-20 w-auto"
                  />
                  <div className="text-right">
                    <div className="text-2xl font-bold text-slate-900">INVOICE</div>
                    <div className="text-sm text-slate-600 mt-1">
                      {viewingInvoice.invoice_number}
                    </div>
                  </div>
                </div>
              </div>

              {/* Dates and Client Info */}
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">Bill To:</h3>
                  <div className="text-sm text-slate-600">
                    <div className="font-medium text-slate-900">{viewingInvoice.client_name}</div>
                    {viewingInvoice.client_company && <div>{viewingInvoice.client_company}</div>}
                    {viewingInvoice.client_address && (
                      <div className="whitespace-pre-line">{viewingInvoice.client_address}</div>
                    )}
                    {viewingInvoice.client_email && <div className="mt-1">{viewingInvoice.client_email}</div>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="space-y-1 text-sm">
                    <div><span className="font-medium">Invoice Date:</span> {formatDate(fromDateOnlyString(viewingInvoice.invoice_date))}</div>
                    <div>
                      <span className="font-medium">Due Date:</span>{' '}
                      {viewingInvoice.due_terms === 'upon_receipt' ? (
                        <span className="text-[#247C75] font-semibold">Due Upon Receipt</span>
                      ) : viewingInvoice.due_date ? (
                        formatDate(fromDateOnlyString(viewingInvoice.due_date))
                      ) : (
                        'Not Set'
                      )}
                    </div>
                    <div className="pt-2">
                      <Badge variant="outline" className={getStatusColor(viewingInvoice.payment_status)}>
                        {viewingInvoice.payment_status.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Description */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Project Description:</h3>
                <p className="text-sm text-slate-600">{viewingInvoice.project_description}</p>
              </div>

              {/* Line Items Table */}
              <table className="w-full mb-8">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-2 text-sm font-semibold text-slate-900">Description</th>
                    <th className="text-center py-2 text-sm font-semibold text-slate-900">
                      {viewingInvoice.line_items[0]?.unit_type === 'sqft' ? 'Sq.ft' : 'Qty'}
                    </th>
                    <th className="text-right py-2 text-sm font-semibold text-slate-900">Unit Price</th>
                    <th className="text-right py-2 text-sm font-semibold text-slate-900">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingInvoice.line_items.map((item, index) => (
                    <tr key={index} className="border-b border-slate-200">
                      <td className="py-3 text-sm text-slate-600">{item.description}</td>
                      <td className="py-3 text-center text-sm text-slate-600">{item.quantity}</td>
                      <td className="py-3 text-right text-sm text-slate-600">{formatCurrency(item.unit_price)}</td>
                      <td className="py-3 text-right text-sm font-medium text-slate-900">{formatCurrency(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end mb-8">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal:</span>
                    <span className="font-medium">{formatCurrency(viewingInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Tax ({viewingInvoice.tax_percentage}%):</span>
                    <span className="font-medium">{formatCurrency(viewingInvoice.tax_amount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t-2 border-slate-300">
                    <span>Total:</span>
                    <span className="text-blue-600">{formatCurrency(viewingInvoice.total)}</span>
                  </div>
                </div>
              </div>

              {/* GST/HST Number */}
              <div className="flex justify-end mb-6">
                <div className="text-sm font-semibold text-slate-700">
                  GST/HST # 736900168RT0001
                </div>
              </div>

              {/* Notes */}
              {viewingInvoice.notes && (
                <div className="border-t pt-4 mb-6">
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">Notes:</h3>
                  <p className="text-sm text-slate-600">{viewingInvoice.notes}</p>
                </div>
              )}

              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-slate-300">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="text-sm text-slate-700 space-y-2">
                    <p className="font-semibold text-slate-900">Make all checks payable to Cleanifax Inc.</p>
                    <p className="font-medium">If you have any questions concerning this invoice, contact:</p>
                    <div className="pl-4">
                      <p className="font-semibold text-blue-600">Grishma Chokshi</p>
                      <p>Phone: 902-223-7377</p>
                      <p>Email: admin@cleanifax.ca</p>
                    </div>
                    <p className="text-center font-bold text-blue-600 text-base mt-4">THANK YOU FOR YOUR BUSINESS!</p>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}


      {/* Status Change Dialog */}
      {statusDialogOpen && selectedInvoiceForStatus && (
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Invoice Status</DialogTitle>
              <DialogDescription>
                Update the payment status for invoice {selectedInvoiceForStatus.invoice_number}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Payment Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="partially_paid">Partially Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="void">Void</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {newStatus === "partially_paid" && (
                <div>
                  <Label>Amount Paid</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter amount paid"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Total: ${selectedInvoiceForStatus.total.toFixed(2)}
                  </p>
                </div>
              )}
              
              <div className="bg-blue-50 p-3 rounded">
                <p className="text-sm text-blue-700">
                  Current status: <strong>{selectedInvoiceForStatus.payment_status}</strong>
                </p>
                {selectedInvoiceForStatus.amount_paid > 0 && (
                  <p className="text-sm text-blue-700 mt-1">
                    Already paid: ${selectedInvoiceForStatus.amount_paid.toFixed(2)}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleStatusChange}>
                Update Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Partial Invoice Dialog */}
      {partialDialogOpen && selectedInvoiceForPartial && (
        <Dialog open={partialDialogOpen} onOpenChange={setPartialDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Partial Invoice</DialogTitle>
              <DialogDescription>
                Invoice a percentage of the total amount for {selectedInvoiceForPartial.invoice_number}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Percentage to Invoice</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={partialPercentage}
                  onChange={(e) => setPartialPercentage(parseFloat(e.target.value))}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter percentage (e.g., 25 for 25%)
                </p>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={partialDueDate}
                  onChange={(e) => setPartialDueDate(e.target.value)}
                />
              </div>
              
              <div className="bg-blue-50 p-3 rounded space-y-1">
                <p className="text-sm text-blue-700">
                  Original Total: <strong>${selectedInvoiceForPartial.total.toFixed(2)}</strong>
                </p>
                <p className="text-sm text-blue-700">
                  Partial Amount ({partialPercentage}%): <strong>${((selectedInvoiceForPartial.total * partialPercentage) / 100).toFixed(2)}</strong>
                </p>
                <p className="text-sm text-blue-700">
                  Remaining Balance: <strong>${(selectedInvoiceForPartial.total - (selectedInvoiceForPartial.total * partialPercentage) / 100).toFixed(2)}</strong>
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPartialDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreatePartial}>
                Create Partial Invoice
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
};

export default InvoicesPage;
